import * as React from 'react';
import { themr } from 'react-css-themr';
import {
    TemperatureType,
    TemperatureThreshold
} from 'common/models/internal/TemperatureThreshold';
import { Select, SelectTypes } from 'common/components/core/select';
import { EnhancedField } from 'common/components/core/form/Field';

import classnames from 'classnames';
import defaultTheme from './LineItemPartsInput.scss';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

class LineItemTemperatureInput extends React.PureComponent {
    constructor(props) {
        super(props);

        this.TemperatureTypeOptions = Object.values(TemperatureType).map(
            temperatureType => ({
                value: temperatureType,
                label: props.t(
                    `${props.translationsObject.tempOptionLabel}.${temperatureType}`
                )
            })
        );

        this.state = {
            temperatureThreshold: props.temperatureThreshold
        };
    }

    componentDidMount() {
        this.setState({
            temperatureThreshold: this.getUpdatedTemperatureThresholdByUnit()
        });
    }

    componentDidUpdate(prevProps) {
        const { temperatureThreshold } = this.props;
        if (prevProps.temperatureThreshold !== temperatureThreshold) {
            this.setState({
                temperatureThreshold
            });
        }
    }

    getUpdatedTemperatureThresholdByUnit = () => {
        const { temperatureThreshold, unitConverter } = this.props;
        return temperatureThreshold.convertToPreferredUnit(unitConverter);
    };

    getTemperatureThreshold() {
        const { temperatureThreshold } = this.state;
        return temperatureThreshold?.temperatureType ||
            temperatureThreshold?.lowerThreshold ||
            temperatureThreshold?.higherThreshold
            ? temperatureThreshold
            : TemperatureThreshold.fromJS({
                  temperatureType: TemperatureType.Chilled
              });
    }

    handleTempMinChange = value => {
        const { onChange } = this.props;
        const temperatureThreshold = this.getTemperatureThreshold();

        onChange && onChange(temperatureThreshold.setLowerThreshold(value));
    };

    handleTempMaxChange = value => {
        const { onChange } = this.props;
        const temperatureThreshold = this.getTemperatureThreshold();

        onChange && onChange(temperatureThreshold.setHigherThreshold(value));
    };

    handleTemperatureThresholdTypeChange = value => {
        const { onChange } = this.props;
        const temperatureThreshold = this.getTemperatureThreshold();

        onChange &&
            onChange(
                temperatureThreshold.setTemperatureType(
                    value ? value.value : null
                )
            );
    };

    onChange = temperatureThreshold => {
        const { onChange } = this.props;

        this.setState({ temperatureThreshold }, () => {
            onChange && onChange(temperatureThreshold);
        });
    };

    render() {
        const {
            theme,
            t,
            translationsObject,
            unitConverter,
            hiddenModelProperties,
            minTempTestId,
            maxTempTestId,
            refigerationTypeTestId
        } = this.props;

        const { temperatureThreshold } = this.state;
        const tempPreferredUnitText = TemperatureThreshold.getPreferredUnitTextForTemp(
            unitConverter
        ).toUpperCase();

        const hiddenTempSubFields = CustomColumnHelpers.getHiddenPropertySubFields(
            {
                key: 'temperatureThreshold',
                hiddenModelProperties
            }
        );

        return (
            <div className={theme.row}>
                {!hiddenTempSubFields.includes('lowerThreshold') ? (
                    <div
                        className={classnames(theme.oneThird, theme.tempInputs)}
                    >
                        <span className={theme.label}>
                            {t(translationsObject.minTempLabel)}
                        </span>
                        <EnhancedField
                            testId={minTempTestId}
                            type="text"
                            element="input"
                            value={
                                temperatureThreshold
                                    ? temperatureThreshold.lowerThreshold
                                    : ''
                            }
                            error={
                                temperatureThreshold &&
                                temperatureThreshold.getValidationMessage(
                                    'lowerThreshold'
                                )
                            }
                            placeholder={t(translationsObject.minTempLabel)}
                            onChange={this.handleTempMinChange}
                            theme={theme}
                            rightIcon={
                                <span>
                                    &deg;
                                    {tempPreferredUnitText}
                                </span>
                            }
                        />
                    </div>
                ) : null}
                {!hiddenTempSubFields.includes('higherThreshold') ? (
                    <div
                        className={classnames(theme.oneThird, theme.tempInputs)}
                    >
                        <span className={theme.label}>
                            {t(translationsObject.maxTempLabel)}
                        </span>
                        <EnhancedField
                            testId={maxTempTestId}
                            type="text"
                            element="input"
                            value={
                                temperatureThreshold
                                    ? temperatureThreshold.higherThreshold
                                    : ''
                            }
                            error={
                                temperatureThreshold &&
                                temperatureThreshold.getValidationMessage(
                                    'higherThreshold'
                                )
                            }
                            placeholder={t(translationsObject.maxTempLabel)}
                            onChange={this.handleTempMaxChange}
                            theme={theme}
                            rightIcon={
                                <span>
                                    &deg;
                                    {tempPreferredUnitText}
                                </span>
                            }
                        />
                    </div>
                ) : null}
                {!hiddenTempSubFields.includes('temperatureType') ? (
                    <div
                        className={classnames(
                            theme.oneThird,
                            theme.tempInputs,
                            theme.last
                        )}
                    >
                        <span className={theme.label}>
                            {t(translationsObject.tempTypeLabel)}
                        </span>
                        <Select
                            testId={refigerationTypeTestId}
                            theme={theme}
                            selectType={SelectTypes.Outline}
                            className={theme.dropdown}
                            options={this.TemperatureTypeOptions}
                            openMenuOnFocus
                            blurInputOnSelect={false}
                            isSearchable={false}
                            isClearable
                            placeholder={t(
                                translationsObject.tempTypePlaceholder
                            )}
                            value={
                                temperatureThreshold
                                    ? temperatureThreshold.temperatureType
                                    : null
                            }
                            onChange={this.handleTemperatureThresholdTypeChange}
                        />
                    </div>
                ) : null}
            </div>
        );
    }
}

export default themr(
    'LineItemTemperatureInput',
    defaultTheme
)(LineItemTemperatureInput);
